import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { UserSidebar } from '@/components/Layout/UserSidebar';
import { planningService, PlanningBuilding, PlanningPayload, PlanningProject } from '@/services/planningService';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTheme } from '@/hooks/useTheme';
import { ProjectLogo } from '@/components/ProjectLogo';

export function PlanningPage() {
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const projectIdInt = projectId ? Number(projectId) : null;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(null);
  const [planning, setPlanning] = useState<PlanningPayload | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [globalTemperature, setGlobalTemperature] = useState<number>(20);

  useEffect(() => {
    if (!projectIdInt) return;
    if (selectedProjectId !== projectIdInt) {
      setSelectedProjectId(projectIdInt);
      setSelectedBuildingId(null);
      setPlanning(null);
    }
  }, [projectIdInt, selectedProjectId]);

  if (!projectIdInt) {
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <span className="material-icons-outlined text-red-500 text-6xl mb-4">error</span>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Projet requis</h2>
            <p className="text-slate-500 dark:text-slate-400 mb-4">Veuillez sélectionner un projet pour accéder au planning.</p>
            <button
              onClick={() => navigate('/dashboard', { replace: true })}
              className="px-4 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              Choisir un projet
            </button>
          </div>
        </div>
      </div>
    );
  }

  const dayLabels: Record<keyof PlanningPayload['week'], string> = {
    mon: 'Lun',
    tue: 'Mar',
    wed: 'Mer',
    thu: 'Jeu',
    fri: 'Ven',
    sat: 'Sam',
    sun: 'Dim',
  };

  const hourLabels = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}h`);

  const {
    data: projects,
    isLoading: isLoadingProjects,
    error: projectsError,
  } = useQuery({
    queryKey: ['planning', 'projects'],
    queryFn: () => planningService.getProjects(),
  });

  const availableProjects = projects ?? [];

  const {
    data: buildings,
    isLoading: isLoadingBuildings,
    error: buildingsError,
  } = useQuery({
    queryKey: ['planning', 'project', selectedProjectId, 'buildings'],
    queryFn: () => planningService.getBuildings(selectedProjectId as number),
    enabled: !!selectedProjectId,
  });

  const availableBuildings = buildings ?? [];

  useEffect(() => {
    if (!selectedProjectId) return;
    if (availableBuildings.length === 1 && selectedBuildingId === null) {
      setSelectedBuildingId(availableBuildings[0].id);
    }
  }, [availableBuildings, selectedBuildingId, selectedProjectId]);

  const {
    data: buildingPlanning,
    isLoading: isLoadingPlanning,
    error: planningError,
  } = useQuery({
    queryKey: ['planning', 'building', selectedBuildingId],
    queryFn: () => planningService.getBuildingPlanning(selectedBuildingId as number),
    enabled: !!selectedBuildingId,
  });

  useEffect(() => {
    if (buildingPlanning?.planning) {
      setPlanning(buildingPlanning.planning);
    }
  }, [buildingPlanning]);

  const updateMutation = useMutation({
    mutationFn: (payload: PlanningPayload) => planningService.updateBuildingPlanning(selectedBuildingId as number, payload),
    onSuccess: (response) => {
      setPlanning(response.planning);
      setLastSavedAt(new Date().toISOString());
    },
  });

  const planningFormSchema = z.object({
    day: z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']),
    startHour: z.number().min(0).max(23),
    endHour: z.number().min(0).max(23),
    temperature: z.number().min(18).max(24),
  }).refine((data) => data.endHour >= data.startHour, {
    message: 'La fin doit être après le début',
    path: ['endHour'],
  });

  const weekFormSchema = z.object({
    startHour: z.number().min(0).max(23),
    endHour: z.number().min(0).max(23),
    temperature: z.number().min(18).max(24),
  }).refine((data) => data.endHour >= data.startHour, {
    message: 'La fin doit être après le début',
    path: ['endHour'],
  });

  type PlanningFormValues = z.infer<typeof planningFormSchema>;
  type WeekFormValues = z.infer<typeof weekFormSchema>;

  const { register, watch, formState: { errors } } = useForm<PlanningFormValues>({
    resolver: zodResolver(planningFormSchema),
    defaultValues: {
      day: 'mon',
      startHour: 0,
      endHour: 5,
      temperature: 20,
    },
  });

  const {
    register: registerWeek,
    watch: watchWeek,
    formState: { errors: errorsWeek },
  } = useForm<WeekFormValues>({
    resolver: zodResolver(weekFormSchema),
    defaultValues: {
      startHour: 0,
      endHour: 5,
      temperature: 20,
    },
  });

  const dayField = register('day');
  const startHourField = register('startHour', { valueAsNumber: true });
  const endHourField = register('endHour', { valueAsNumber: true });
  const temperatureField = register('temperature', { valueAsNumber: true });

  const startHourWeekField = registerWeek('startHour', { valueAsNumber: true });
  const endHourWeekField = registerWeek('endHour', { valueAsNumber: true });
  const temperatureWeekField = registerWeek('temperature', { valueAsNumber: true });

  const watched = watch();
  const watchedWeek = watchWeek();

  const handleApplySelection = () => {
    if (!planning || !selectedBuildingId) return;
    if (Object.keys(errors).length > 0) return;

    const nextPlanning: PlanningPayload = {
      timezone: planning.timezone || 'Europe/Paris',
      week: {
        ...planning.week,
        [watched.day]: planning.week[watched.day].map((value, idx) => {
          if (idx < watched.startHour || idx > watched.endHour) {
            return value;
          }
          return watched.temperature;
        }),
      },
    };

    setPlanning(nextPlanning);
    updateMutation.mutate(nextPlanning);
  };

  const handleApplyWeekSelection = () => {
    if (!planning || !selectedBuildingId) return;
    if (Object.keys(errorsWeek).length > 0) return;

    const nextPlanning: PlanningPayload = {
      timezone: planning.timezone || 'Europe/Paris',
      week: Object.fromEntries(
        (Object.keys(planning.week) as Array<keyof PlanningPayload['week']>).map((day) => [
          day,
          planning.week[day].map((value, idx) => {
            if (idx < watchedWeek.startHour || idx > watchedWeek.endHour) {
              return value;
            }
            return watchedWeek.temperature;
          }),
        ])
      ) as PlanningPayload['week'],
    };

    setPlanning(nextPlanning);
    updateMutation.mutate(nextPlanning);
  };

  const selectedProject = useMemo<PlanningProject | null>(() => {
    return availableProjects.find((p) => p.id === selectedProjectId) ?? null;
  }, [availableProjects, selectedProjectId]);

  const selectedBuilding = useMemo<PlanningBuilding | null>(() => {
    return availableBuildings.find((b) => b.id === selectedBuildingId) ?? null;
  }, [availableBuildings, selectedBuildingId]);

  const formatSavedAt = (iso: string | null) => {
    if (!iso) return 'Jamais';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return 'Jamais';
    return date.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const getTempColor = (temp: number | null) => {
    if (temp === null || temp === undefined) return 'bg-slate-100 dark:bg-slate-800/40 text-slate-400 dark:text-slate-500';
    if (temp >= 23) return 'bg-orange-500/10 dark:bg-orange-500/20 text-orange-700 dark:text-orange-200 border-orange-500/30';
    if (temp >= 20) return 'bg-amber-500/10 dark:bg-amber-500/20 text-amber-700 dark:text-amber-200 border-amber-500/30';
    if (temp >= 18) return 'bg-cyan-500/10 dark:bg-cyan-500/20 text-cyan-700 dark:text-cyan-200 border-cyan-500/30';
    return 'bg-blue-500/10 dark:bg-blue-500/20 text-blue-700 dark:text-blue-200 border-blue-500/30';
  };

  const selectClass = "w-full rounded-lg bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 px-3 py-2 text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-primary text-sm";

  return (
    <div className="bg-slate-50 text-slate-900 dark:bg-slate-900 dark:text-slate-100 h-screen flex overflow-hidden font-display">
      <UserSidebar projectId={projectIdInt} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative bg-slate-50 dark:bg-slate-900">
        {/* Header */}
        <header className="h-16 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-6 sm:px-8 z-20 shadow-sm sticky top-0">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <span className="material-icons-outlined" style={{ fontSize: '20px' }}>menu</span>
            </button>
            <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
              Planning
            </h1>
          </div>
          <div className="flex items-center space-x-3">
            <div className="hidden md:flex items-center px-3 py-1 bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-full text-xs font-medium text-slate-600 dark:text-slate-300">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse shadow-sm shadow-green-500/50"></span>
              Système en ligne
            </div>
            <div className="h-8 w-px bg-slate-200 dark:bg-slate-700 mx-1"></div>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-primary transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
              title={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {isDark ? <span className="material-icons-outlined">light_mode</span> : <span className="material-icons-outlined">dark_mode</span>}
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900">
          {isLoadingProjects ? (
            <div className="text-center text-slate-500 dark:text-slate-400">Chargement des projets...</div>
          ) : projectsError ? (
            <div className="text-center text-slate-500 dark:text-slate-400">Erreur lors du chargement des projets.</div>
          ) : (
            <div className="space-y-6">
              {/* Project / Building selectors */}
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                  <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Projet</p>
                  <select
                    value={selectedProjectId ?? ''}
                    className={selectClass}
                    disabled={true}
                  >
                    {availableProjects.map((p) => (
                      <option key={p.id} value={p.id}>{p.title}</option>
                    ))}
                  </select>
                  {selectedProject ? (
                    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{selectedProject.city ?? '—'}</p>
                  ) : null}
                </div>

                <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                  <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Immeuble</p>
                  <select
                    value={selectedBuildingId ?? ''}
                    onChange={(e) => {
                      const nextId = e.target.value ? Number(e.target.value) : null;
                      setSelectedBuildingId(nextId);
                    }}
                    className={selectClass}
                    disabled={!selectedProjectId || isLoadingBuildings}
                  >
                    <option value="">Sélectionner un immeuble</option>
                    {availableBuildings.map((b) => (
                      <option key={b.id} value={b.id}>{b.name ?? `Immeuble ${b.id}`}</option>
                    ))}
                  </select>
                  {isLoadingBuildings ? (
                    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Chargement des immeubles...</p>
                  ) : buildingsError ? (
                    <p className="mt-2 text-xs text-red-400">Erreur de chargement</p>
                  ) : null}
                </div>

                <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-4 flex flex-col justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Dernière sauvegarde</p>
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{formatSavedAt(lastSavedAt)}</p>
                  </div>
                  <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    {updateMutation.isPending ? 'Enregistrement en cours...' : 'Sauvegarde automatique activée'}
                  </div>
                </div>
              </div>

              {/* Planner */}
              {!selectedBuildingId ? (
                <div className="text-center text-slate-500 dark:text-slate-400 py-12">Sélectionnez un immeuble pour afficher le planning.</div>
              ) : isLoadingPlanning ? (
                <div className="text-center text-slate-500 dark:text-slate-400 py-12">Chargement du planning...</div>
              ) : planningError ? (
                <div className="text-center text-red-400 py-12">Erreur lors du chargement du planning.</div>
              ) : planning ? (
                <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
                  <div className="xl:col-span-1 bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-2xl p-5 space-y-4">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">Immeuble</p>
                      <h2 className="text-lg font-bold text-slate-900 dark:text-white">{selectedBuilding?.name ?? `Immeuble ${selectedBuildingId}`}</h2>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{selectedProject?.title ?? ''}</p>
                    </div>

                    <div className="space-y-3">
                      <div className="bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                        <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Appliquer par jours</p>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-slate-500 dark:text-slate-400">Jour</label>
                            <select {...dayField} className={`mt-1 ${selectClass}`}>
                              {Object.entries(dayLabels).map(([key, label]) => (
                                <option key={key} value={key}>{label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 dark:text-slate-400">Température</label>
                            <select {...temperatureField} className={`mt-1 ${selectClass}`}>
                              {Array.from({ length: 13 }, (_, i) => {
                                const temp = 18 + i * 0.5;
                                return (
                                  <option key={temp} value={temp}>
                                    {temp.toFixed(1)}°C
                                  </option>
                                );
                              })}
                            </select>
                            {errors.temperature && <p className="text-xs text-red-400 mt-1">{errors.temperature.message}</p>}
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 dark:text-slate-400">Début</label>
                            <select {...startHourField} className={`mt-1 ${selectClass}`}>
                              {Array.from({ length: 24 }, (_, i) => (
                                <option key={i} value={i}>{String(i).padStart(2, '0')}h</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 dark:text-slate-400">Fin</label>
                            <select {...endHourField} className={`mt-1 ${selectClass}`}>
                              {Array.from({ length: 24 }, (_, i) => (
                                <option key={i} value={i}>{String(i).padStart(2, '0')}h</option>
                              ))}
                            </select>
                            {errors.endHour && <p className="text-xs text-red-400 mt-1">{errors.endHour.message}</p>}
                          </div>
                        </div>
                        <button
                          onClick={handleApplySelection}
                          disabled={!planning || Object.keys(errors).length > 0 || updateMutation.isPending}
                          className="mt-3 w-full px-4 py-2 bg-primary/20 border border-primary/30 text-primary rounded-lg text-sm font-semibold hover:bg-primary/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          <span className="material-icons-outlined" style={{ fontSize: '18px' }}>check</span>
                          Appliquer
                        </button>
                      </div>

                      <div className="bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                        <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Appliquer par semaine</p>
                        <p className="text-xs text-slate-400 dark:text-slate-500 mb-3">Même plage horaire pour tous les jours</p>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-slate-500 dark:text-slate-400">Température</label>
                            <select {...registerWeek('temperature', { valueAsNumber: true })} className={`mt-1 ${selectClass}`}>
                              {Array.from({ length: 13 }, (_, i) => {
                                const temp = 18 + i * 0.5;
                                return (
                                  <option key={temp} value={temp}>{temp.toFixed(1)}°C</option>
                                );
                              })}
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 dark:text-slate-400">Début</label>
                            <select {...registerWeek('startHour', { valueAsNumber: true })} className={`mt-1 ${selectClass}`}>
                              {Array.from({ length: 24 }, (_, i) => (
                                <option key={i} value={i}>{String(i).padStart(2, '0')}h</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 dark:text-slate-400">Fin</label>
                            <select {...registerWeek('endHour', { valueAsNumber: true })} className={`mt-1 ${selectClass}`}>
                              {Array.from({ length: 24 }, (_, i) => (
                                <option key={i} value={i}>{String(i).padStart(2, '0')}h</option>
                              ))}
                            </select>
                            {errorsWeek.endHour && <p className="text-xs text-red-400 mt-1">{errorsWeek.endHour.message}</p>}
                          </div>
                        </div>
                        <button
                          onClick={handleApplyWeekSelection}
                          disabled={!planning || Object.keys(errorsWeek).length > 0 || updateMutation.isPending}
                          className="mt-3 w-full px-4 py-2 bg-primary/20 border border-primary/30 text-primary rounded-lg text-sm font-semibold hover:bg-primary/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          <span className="material-icons-outlined" style={{ fontSize: '18px' }}>date_range</span>
                          Appliquer à la semaine
                        </button>
                      </div>

                      <div className="bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                        <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Légende</p>
                        <div className="space-y-2 text-xs text-slate-700 dark:text-slate-300">
                          <div className="flex items-center gap-2"><span className="w-4 h-4 rounded bg-blue-500/20 border border-blue-500/30"></span> &lt; 18°C</div>
                          <div className="flex items-center gap-2"><span className="w-4 h-4 rounded bg-cyan-500/20 border border-cyan-500/30"></span> 18-19.9°C</div>
                          <div className="flex items-center gap-2"><span className="w-4 h-4 rounded bg-amber-500/20 border border-amber-500/30"></span> 20-22.9°C</div>
                          <div className="flex items-center gap-2"><span className="w-4 h-4 rounded bg-orange-500/20 border border-orange-500/30"></span> ≥ 23°C</div>
                        </div>
                      </div>

                      <div className="bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                        <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">Remplir tout le planning</p>
                        <div className="space-y-3">
                          <div>
                            <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Température</label>
                            <select
                              value={globalTemperature}
                              onChange={(e) => setGlobalTemperature(Number(e.target.value))}
                              className={selectClass}
                            >
                              {Array.from({ length: 13 }, (_, i) => {
                                const temp = 18 + i * 0.5;
                                return (
                                  <option key={temp} value={temp}>{temp.toFixed(1)}°C</option>
                                );
                              })}
                            </select>
                          </div>
                          <button
                            onClick={() => {
                              if (!planning || !selectedBuildingId) return;
                              const newPlanning: PlanningPayload = {
                                timezone: planning.timezone || 'Europe/Paris',
                                week: {
                                  mon: Array(24).fill(globalTemperature),
                                  tue: Array(24).fill(globalTemperature),
                                  wed: Array(24).fill(globalTemperature),
                                  thu: Array(24).fill(globalTemperature),
                                  fri: Array(24).fill(globalTemperature),
                                  sat: Array(24).fill(globalTemperature),
                                  sun: Array(24).fill(globalTemperature),
                                },
                              };
                              setPlanning(newPlanning);
                              updateMutation.mutate(newPlanning);
                            }}
                            disabled={!planning || updateMutation.isPending}
                            className="w-full px-4 py-2 bg-primary/20 border border-primary/30 text-primary rounded-lg text-sm font-semibold hover:bg-primary/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            <span className="material-icons-outlined" style={{ fontSize: '18px' }}>auto_fix_high</span>
                            Appliquer à toute la semaine
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="xl:col-span-3">
                    <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-2xl p-5">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">Planning hebdomadaire</p>
                          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Par heure</h3>
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">{planning.timezone}</div>
                      </div>

                      <div className="overflow-x-auto">
                        <div className="min-w-[760px]">
                          <div className="grid grid-cols-[80px_repeat(7,minmax(90px,1fr))] gap-2 text-xs text-slate-500 dark:text-slate-400 mb-2">
                            <div></div>
                            {Object.entries(dayLabels).map(([key, label]) => (
                              <div key={key} className="text-center font-semibold text-slate-700 dark:text-slate-300">{label}</div>
                            ))}
                          </div>

                          <div className="grid gap-2">
                            {hourLabels.map((hourLabel, hourIdx) => (
                              <div key={hourLabel} className="grid grid-cols-[80px_repeat(7,minmax(90px,1fr))] gap-2">
                                <div className="text-xs text-slate-400 dark:text-slate-500 flex items-center">{hourLabel}</div>
                                {Object.keys(dayLabels).map((dayKey) => {
                                  const day = dayKey as keyof PlanningPayload['week'];
                                  const value = planning.week[day][hourIdx] ?? null;
                                  return (
                                    <div
                                      key={`${day}-${hourLabel}`}
                                      className={`rounded-lg border border-slate-200 dark:border-slate-700/60 px-2 py-1 text-center text-xs ${getTempColor(value)}`}
                                    >
                                      {value !== null ? `${value.toFixed(1)}°` : '—'}
                                    </div>
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
